import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';

import { LogService } from './log.service';

/**
 * Device Service Properties and Methods
 * 
 * Properties were moved from search.component.ts
 * 
 * Notes:
 * 
  // new ipads are retina 2048 x 1536
  // need to treat them link old 1024 x 768
  // TODO: detect retina
  // var query = "(-webkit-min-device-pixel-ratio: 2), (min-device-pixel-ratio: 2), (min-resolution: 192dpi)";
  // if (matchMedia(query).matches) { } else { }
 */
@Injectable({ providedIn: 'root' })
export class DeviceService {
  private readonly _serviceName = 'DeviceService';

  private readonly retinaMediaQuery = 'screen and (device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2)';

  constructor(private log: LogService, private device: DeviceDetectorService) {
    // console.log(`${this._serviceName}.CTOR deviceInfo`, device.getDeviceInfo());
  }

  /**
   * Property indicating if the device is a retina device
   */
  public get isRetina(): boolean {
    const returnValue = matchMedia(this.retinaMediaQuery).matches;

    // console.log(`${this._serviceName}.isRetina =${returnValue}`);
    

    return returnValue;
  }


  /**
   * Gets device information using the DeviceDetectorService 
   */
  public getDeviceInfo() {
    return this.device.getDeviceInfo();  
  }

  


  /**
   * Property indicating if the device is a tablet device
   */
  public get isTablet(): boolean {
    const returnValue = this.device.isTablet();

    // console.log(`${this._serviceName}.isTablet =${returnValue}`);

    return returnValue;
  }

  /**
   * Property indicating if the device is a desktop device
   */
  public get isDesktop(): boolean {
    const returnValue = this.device.isDesktop();

    // console.log(`${this._serviceName}.isDesktop =${returnValue}`);

    return returnValue;
  }

  /**
   * Property for whether the device is in Portrait orientation. For OA, equal width/height is treated as portrait
   */
  public get isPortrait(): boolean {
    const returnValue = window.innerWidth <= window.innerHeight;

    // console.log(`${this._serviceName}.isPortrait =${returnValue}`);

    return returnValue;
  }

  /**
   * Property for whether the device is in Landscape orientation. For OA, equal width/height is treated as portrait
   */
  public get isLandscape(): boolean {
    const returnValue = window.innerWidth > window.innerHeight;

    // console.log(`${this._serviceName}.isLandscape =${returnValue}`);

    return returnValue;
  }

  /**
   * Property indicating if the device both a tablet and in portrait
   */
  public get isTabletPortrait(): boolean {
    if (!this.isTablet) {
      // console.log(`${this._serviceName}.isTabletPortrait  returnValue=false.  isTablet == false`);
      return false;
    }

    if (this.isPortrait) {
      // console.log(`${this._serviceName}.isTabletPortrait  returnValue=false.  isPortrait == true`);
      return true;
    }

    // console.log(`${this._serviceName}.isTabletPortrait  returnValue=false.  No other conditions were met.`);

    return false;

    // return this.getLegacyIsTabletPortrait();
  }

  /**
   * Property indicating if the device is mobile, according to the OA Platform Mobile Definition
   */
  public get isMobile(): boolean {
    console.log(
      `${
        this._serviceName
      }.isMobile  this.device.isMobile()=${this.device.isMobile()}, device.isDesktop()=${this.device.isDesktop()}, device.isTablet()=${this.device.isTablet()} `
    );

    // this is the ngx-device-detector's "isMobile" implementation.
    // if there are problems, we can fall back to (and potentially modify) getLegacyIsMobile
    return this.device.isMobile();

    // return this.getLegacyIsMobile();
  }

  /**
   * Original isMobile implementation added by Aric and refactored into
   * a method on the Device Service by Jay.
   */
  private getLegacyIsMobile() {
    const innerWidth = window.innerWidth;

    console.log(
      `${
        this._serviceName
      }.deviceIsMobile  this.device.isMobile()=${this.device.isMobile()}, device.isDesktop()=${this.device.isDesktop()}, device.isTablet()=${this.device.isTablet()} `
    );

    const deviceIsMobile = this.device.isMobile();

    // console.log(`${this._serviceName}.deviceIsMobile  window.navigator.userAgent=`,window.navigator.userAgent);
    console.log(`${this._serviceName}.deviceIsMobile  this.device.device=`, this.device);

    if (this.isRetina) {
      // TODO: Need to look at what actually should go here.  This condition can be true on non-mobile retina devices.
      if (window.innerWidth < window.innerHeight) {
        console.log(`${this._serviceName}.isMobile  returnValue=true.  Retina and window  innerWidth is less than the innerHeight`);
        return true;
      }

      console.log(`${this._serviceName}.isMobile  returnValue=false.  Retina and window  innerWidth is  >= the innerHeight`);
      return true;
    } else {
      if (!innerWidth) {
        console.log(`${this._serviceName}.isMobile  returnValue=false.  Not Retina and window returns no innerWidth value.`);
        return false;
      }

      if (innerWidth < 800) {
        console.log(`${this._serviceName}.isMobile  returnValue=true.  Not Retina and innerWidth(${innerWidth}) < 800`);
        return true;
      }

      if (innerWidth >= 800) {
        console.log(`${this._serviceName}.isMobile  returnValue=false.  Not Retina and innerWidth(${innerWidth}) >= 800`);
        return false;
      }
    }

    console.log(`${this._serviceName}.isMobile  returnValue=false.  No other conditions were met.`);

    return false;
  }

  /**
   * Legacy "isTabletPortrait" imeplementation
   */
  private getLegacyIsTabletPortrait(): boolean {
    if (!this.isMobile) {
      console.log(`${this._serviceName}.isTabletPortrait  returnValue=false.  isMobile == false`);
      return false;
    }

    if (!this.isPortrait) {
      console.log(`${this._serviceName}.isTabletPortrait  returnValue=false.  isPortrait == false`);
      return false;
    }

    // This is the current OA definition, which can be problematic since these widths can be obtained my non-iOS tablets and scaling
    if (window.innerWidth === 768 || window.innerWidth === 1536 || window.innerWidth === 1024) {
      console.log(`${this._serviceName}.isTabletPortrait  returnValue=true.  window.innerWidth === ${window.innerWidth}`);
      return true;
    }

    console.log(`${this._serviceName}.isTabletPortrait  returnValue=false.  No other conditions were met.`);

    return false;
  }

  /**
   * Gets the device pixel ration
   * https://stackoverflow.com/a/29913175/386619 
   */
  public getDevicePixelRatio() {
    // tslint:disable-next-line: no-any
    const screen = (window.screen as any);
    let ratio = 1;
    // To account for zoom, change to use deviceXDPI instead of systemXDPI
    if (
      screen.systemXDPI !== undefined &&
      screen !== undefined &&
      screen > screen.logicalXDPI
    ) {
      // Only allow for values > 1
      ratio = screen.systemXDPI / screen.logicalXDPI;
    } else if (window.devicePixelRatio !== undefined) {
      ratio = window.devicePixelRatio;
    }
    return ratio;
  }
}
